import Equipment, { EquipmentParams } from './equipment';
import Ship from './ship';

import { getIntermediateColor, RGBA } from '../../../utils/color';
import drawLifeBar from '../../../utils/lifeBar';


export interface ShieldParams extends EquipmentParams {
  radius?: number;
  HP?: number;
  regenerationPerSecond?: number;
  regenerationEnergyCostRatio?: number;
  rebootDuration?: number;
}

export default class Shield extends Equipment implements ShieldParams {
  radius = 100;
  HP = 100;
  regenerationPerSecond = 10;
  regenerationEnergyCostRatio = 2; // cost for each HP regenerated
  rebootDuration = 10000; // ms

  HPMax: number;
  disabled = false;
  rebootDurationDone = 0;

  constructor(params: ShieldParams) {
    super(params);
    this.applyParams(params);

    this.HPMax = this.HP;
  }

  setHP(value: number) {
    this.HP = Math.min(
      Math.max(0, value),
      this.HPMax
    );
  }

  equipTo(ship: Ship): Ship {
    super.equipTo(ship);
    this.ship.shield = this;
    return ship;
  }

  update(modifier: number): Shield {
    this.updatePosition();

    if (!this.disabled) // shield regeneration
    {

      const healAmount = this.regenerationPerSecond * modifier;
      const energyCost = healAmount * this.regenerationEnergyCostRatio;
      if (
        this.HP < this.HPMax
        && this.ship?.generator?.energy >= energyCost
      ) {
        this.ship.generator.energy -= energyCost;
        this.heal(healAmount);
      }
    }
    else if (this.ship.generator) // shield reboot
    {
      this.rebootDurationDone += modifier * 1000;
      if (this.rebootDurationDone >= this.rebootDuration) {
        this.rebootDurationDone = 0;
        this.disabled = false;
        this.setHP(this.HPMax);
      }
    }

    return this;
  }

  draw(): CanvasRenderingContext2D {
    this.drawRebootBar();

    const context = this.ship.context;
    if (this.HP <= 0)
      return context;

    const shieldColor = getIntermediateColor({
      startColor: { R: 0, G: 0, B: 255, A: 1 },
      endColor: { R: 100, G: 0, B: 0, A: 1 },
      ratio: this.HP / this.HPMax
    });

    const shieldGradient = context.createRadialGradient(this.x, this.y, this.radius / 2, this.x, this.y, this.radius);
    shieldGradient.addColorStop(0, 'transparent');
    shieldGradient.addColorStop(1, RGBA.toString(shieldColor));

    context.save();
    context.fillStyle = shieldGradient;
    context.beginPath();
    context.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
    context.fill();
    context.restore();
    return context;
  }

  drawRebootBar() {
    if (!this.disabled || !this.ship.generator) return;

    const shieldBarColor = { R: 0, G: 0, B: 255, A: 1 };
    const ratio = this.rebootDurationDone / this.rebootDuration

    drawLifeBar({
      x: this.ship.x - this.ship.width / 2,
      y: this.ship.y + this.ship.height / 3,
      width: this.ship.width,
      height: this.ship.width / 10,
      ratio,
      context: this.ship.context,
      startColor: shieldBarColor,
      endColor: shieldBarColor,
    });
  }

  takeDamages(amount: number): number {
    this.HP -= amount;
    if (this.HP <= 0) {
      amount = Math.abs(this.HP);
      this.HP = 0;
      this.disabled = true;
      // TODO sound effect as the shield crumbles
    }
    else
      amount = 0; // aborb every damage

    return amount; // return the remaining damages to be taken by the ship
  }

  heal(amount: number): void {
    Ship.prototype.heal.call(this, amount);
  }
}
